import { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { sprintf } from 'sprintf-js';

import Banner, { BannerProps } from '../Banner';
import type { JumbotronProps } from 'modules/common/components/Jumbotron';
import RequestFormEntryForm from './RequestFormEntryForm';
import HomeRequestFormEntry from './HomeRequestFormEntry';
import { ReduxProps } from './BannerZipcode.container';
import type { VerticalPageJobType } from 'modules/job-types/types';
import { proCountSelector } from 'modules/marketing-pages/duck';
import {
  useGoogleOptimize,
  GOOGLE_OPTIMIZE_HOME_REQUEST_ENTRY_TEST_ID,
} from 'utils/google-optimize-manager';

import styles from './BannerZipcode.module.scss';

const MINIMUM_TO_SHOW_PRO_COUNT = 30;

interface OwnProps {
  bannerProps?: Pick<BannerProps, 'shouldUseOverlayPadding' | 'shouldUseOverlayBackgroundColor'>;
  bgImg: string;
  bgImgObjectFit?: 'cover' | 'contain';
  city?: { key?: string; name: string };
  cityGroupName?: string;
  isJumbotronResponsive?: boolean;
  isRequestFormEntryEnabled?: boolean;
  jobType: VerticalPageJobType;
  jumbotronProps?: Omit<JumbotronProps, 'bgImg'>;
  prefecture?: { key?: string; name: string };
  shouldBannerBeFullHeight?: boolean;
}

// https://optimize.google.com/optimize/home/?authuser=1#/accounts/4704527235/containers/14842561/experiments/274
const GOOGLE_OPTIMIZE_HOME_REQUEST_ENTRY_TEST_VARIATION = '1';
const GOOGLE_OPTIMIZE_DURATION = 100;

type Props = OwnProps & ReduxProps;

const BannerZipcode: FC<Props> = ({
  bgImg,
  city = { name: '' },
  cityGroupName = '',
  bannerProps = {},
  isRequestFormEntryEnabled = true,
  jobType,
  jumbotronProps = {},
  openRequestModal,
  prefecture = { name: '' },
  bgImgObjectFit,
  isJumbotronResponsive,
  shouldBannerBeFullHeight,
  translation,
}) => {
  const { t } = useTranslation();
  const proCount = useSelector(proCountSelector);

  useEffect(() => {
    if (jobType.isConciergeBooking) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      (async () => {
        await openRequestModal({ city, jobTypeId: jobType.id, prefecture });
      })();
    }
  }, [city, jobType.isConciergeBooking, jobType.id, openRequestModal, prefecture]);

  const { landingPageSubtitle = '', requestType = '', seoName } = jobType.translations;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const jobTypeName = seoName || requestType;

  const title =
    jobType.isDirectBooking || jobType.isConciergeBooking
      ? jobTypeName
      : sprintf(translation.bannerTitle4, {
          jobType: jobTypeName,
          jobTypeTitle2: jobTypeName,
          prosCount:
            proCount >= MINIMUM_TO_SHOW_PRO_COUNT
              ? `${proCount.toLocaleString()}${t(
                  'marketingPages:requestFormEntryForm.banner.proCount.withProCount'
                )}`
              : t('marketingPages:requestFormEntryForm.banner.proCount.withoutProCount'),
        });

  const entryFormLabel = jobType.isDirectBooking
    ? landingPageSubtitle
    : t('marketingPages:requestFormEntryForm.label', {
        interpolation: { escapeValue: false },
        jobType: requestType,
      });

  const hasAtleastOneTitleString = prefecture.name || city.name || cityGroupName || title;

  const googleOptimizeVal = useGoogleOptimize(
    GOOGLE_OPTIMIZE_HOME_REQUEST_ENTRY_TEST_ID,
    GOOGLE_OPTIMIZE_DURATION
  );
  const isHomeTestVariation =
    googleOptimizeVal === GOOGLE_OPTIMIZE_HOME_REQUEST_ENTRY_TEST_VARIATION;

  const EntryForm =
    jobType.shouldRequestFlowStartWithQuestion && isHomeTestVariation ? (
      <HomeRequestFormEntry
        firstField={jobType.forms[0].fields[0]}
        label={entryFormLabel}
        // eslint-disable-next-line react/jsx-no-bind
        onSubmit={async (propertyType) => {
          await openRequestModal({
            city,
            cityGroupName,
            jobTypeId: jobType.id,
            prefecture,
            propertyType,
          });
        }}
      />
    ) : (
      <RequestFormEntryForm
        isZipcodeInputShown={!jobType.isConciergeBooking}
        label={entryFormLabel}
        // eslint-disable-next-line react/jsx-no-bind
        onSubmit={async (address) => {
          await openRequestModal({
            address,
            city,
            cityGroupName,
            jobTypeId: jobType.id,
            prefecture,
          });
        }}
      />
    );

  return (
    <>
      <Banner
        bgImg={bgImg}
        alt={jobType.title}
        bgImgObjectFit={bgImgObjectFit}
        fullHeight={shouldBannerBeFullHeight}
        isJumbotronResponsive={isJumbotronResponsive}
        jumbotronProps={jumbotronProps}
        jobTypeId={jobType.id}
        {...bannerProps}
      >
        {hasAtleastOneTitleString && (
          <h1 className={styles.header}>
            {prefecture.name}
            {cityGroupName}
            {city.name}
            {(prefecture.name || city.name || cityGroupName) && <br />}
            {title}
          </h1>
        )}
        {isRequestFormEntryEnabled && EntryForm}
      </Banner>
    </>
  );
};

export type { OwnProps as BannerZipcodeProps };
export default BannerZipcode;
